import React, { useState } from "react";
import { AlertCircleIcon, Loader2Icon } from "lucide-react";
import { TextField } from "../ui/TextField";
import { DEMO_ACCOUNT, useAuth } from "../../contexts/AuthContext";
type Mode = "login" | "signup";
interface AuthFormProps {
  onSuccess?: () => void;
  defaultMode?: Mode;
}
type FieldErrors = Partial<Record<"firstName" | "lastName" | "email" | "phone" | "password", string>>;
export function AuthForm({
  onSuccess,
  defaultMode = "login"
}: AuthFormProps) {
  const {
    login,
    signup
  } = useAuth();
  const [mode, setMode] = useState<Mode>(defaultMode);
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    password: ""
  });
  const [errors, setErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const update = (key: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm(f => ({
      ...f,
      [key]: e.target.value
    }));
    setErrors(er => ({
      ...er,
      [key]: undefined
    }));
  };
  const switchMode = (next: Mode) => {
    setMode(next);
    setErrors({});
    setFormError(null);
  };
  const validate = (): FieldErrors => {
    const next: FieldErrors = {};
    if (!/^\S+@\S+\.\S+$/.test(form.email)) next.email = "Saisissez une adresse e-mail valide.";
    if (mode === "login" && !form.password) next.password = "Saisissez votre mot de passe.";
    if (mode === "signup") {
      if (!form.firstName.trim()) next.firstName = "Prénom requis.";
      if (!form.lastName.trim()) next.lastName = "Nom requis.";
      if (form.phone.replace(/\D/g, "").length < 10) next.phone = "Numéro de téléphone invalide.";
      if (form.password.length < 8) next.password = "8 caractères minimum.";
    }
    return next;
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const found = validate();
    setErrors(found);
    if (Object.keys(found).length > 0) return;
    setSubmitting(true);
    setFormError(null);
    try {
      if (mode === "login") await login(form.email, form.password);else await signup(form);
      onSuccess?.();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : "Une erreur est survenue.");
    } finally {
      setSubmitting(false);
    }
  };
  return <div data-id="element-36" data-mp="y8yisdjcqndl">
      <div role="tablist" aria-label="Type de compte" className="grid grid-cols-2 rounded-full bg-surface p-1" data-id="element-37" data-mp="y8q90uiizy6m">
        {(["login", "signup"] as const).map(m => <button key={m} type="button" role="tab" aria-selected={mode === m} onClick={() => switchMode(m)} className={`rounded-full py-2.5 text-sm font-medium transition-colors duration-150 ${mode === m ? "bg-white text-ink shadow-sm" : "text-muted hover:text-ink"}`} data-id="element-38" data-mp="17727fipmw09m">
            {m === "login" ? "Se connecter" : "Créer un compte"}
          </button>)}
      </div>

      <form onSubmit={handleSubmit} noValidate className="mt-6 space-y-4" data-id="element-39" data-mp="202rtabdodo09">
        {mode === "signup" && <div className="grid gap-4 sm:grid-cols-2" data-id="element-40" data-mp="1vaqk4ybt0deg">
            <TextField id="firstName" label="Prénom" autoComplete="given-name" value={form.firstName} onChange={update("firstName")} error={errors.firstName} data-id="element-41" data-mp="1ra7sb49wv7c9" />
            <TextField id="lastName" label="Nom" autoComplete="family-name" value={form.lastName} onChange={update("lastName")} error={errors.lastName} data-id="element-42" data-mp="1mcoe24d7qukb" />
          </div>}
        <TextField id="email" type="email" label="Adresse e-mail" autoComplete="email" value={form.email} onChange={update("email")} error={errors.email} data-id="element-43" data-mp="2f0tz6s86ilko" />
        {mode === "signup" && <TextField id="phone" type="tel" label="Téléphone" autoComplete="tel" placeholder="06 00 00 00 00" value={form.phone} onChange={update("phone")} error={errors.phone} data-id="element-44" data-mp="252e82p95jcb6" />}
        <TextField id="password" type="password" label="Mot de passe" autoComplete={mode === "login" ? "current-password" : "new-password"} value={form.password} onChange={update("password")} error={errors.password} data-id="element-45" data-mp="tlwmjk73eu3b" />

        {formError && <p role="alert" className="flex items-start gap-2 rounded-xl bg-danger/5 px-4 py-3 text-sm text-danger" data-id="element-46" data-mp="28jifbngfjpvn">
            <AlertCircleIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" data-id="element-47" data-mp="2di237tel3bqn" />
            {formError}
          </p>}

        <button type="submit" disabled={submitting} className="flex w-full items-center justify-center gap-2 rounded-full bg-brand py-3.5 text-sm font-medium text-white transition-colors duration-150 hover:bg-brand-dark disabled:cursor-not-allowed disabled:opacity-70" data-id="element-48" data-mp="315ab8j3ecywd">
          {submitting && <Loader2Icon className="h-4 w-4 animate-spin" aria-hidden="true" data-id="element-49" data-mp="1ft8u6sqork0a" />}
          {mode === "login" ? "Se connecter" : "Créer mon compte"}
        </button>
      </form>

      {mode === "login" && <p className="mt-5 text-xs leading-relaxed text-muted" data-id="element-50" data-mp="1d8u9gi2tw439">
          Compte de démonstration : {DEMO_ACCOUNT.email} / {DEMO_ACCOUNT.password}{" "}
          <button type="button" onClick={() => setForm(f => ({
        ...f,
        email: DEMO_ACCOUNT.email,
        password: DEMO_ACCOUNT.password
      }))} className="font-medium text-brand underline-offset-2 hover:underline" data-id="element-51" data-mp="18cddj1pe0lfm">
            Remplir
          </button>
        </p>}
    </div>;
}